import type {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext } from 'react';
import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  INursingHomePhoenixApi,
  NursingHome,
  NursingHomeUpdate,
  TenantInformation,
} from './api';
import { NursingHomePhoenixApi } from './api';
import type { BaseData, LegalEntity } from '@pflegenavi/shared/api';
import { useAuthentication } from '@pflegenavi/shared-frontend/authentication';

const ApiContext = createContext<INursingHomePhoenixApi | undefined>(undefined);
const {
  useApi: useNursingHomePhoenixApi,
  ApiProvider: NursingHomePhoenixApiProvider,
} = makeApiProvider({
  name: 'NursingHomePhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new NursingHomePhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useNursingHomePhoenixApi,
  NursingHomePhoenixApiProvider,
  ApiContext as NursingHomePhoenixApiContext,
};

export type { NursingHome, NursingHomeUpdate, TenantInformation };

const LEGAL_ENTITIES_KEY: QueryKey = ['LEGAL_ENTITIES_KEY'];
const CREATE_LEGAL_ENTITY_KEY: QueryKey = ['CREATE_LEGAL_ENTITY_KEY'];
const UPDATE_LEGAL_ENTITY_KEY: QueryKey = ['UPDATE_LEGAL_ENTITY_KEY'];
const TENANT_INFORMATION_KEY: QueryKey = ['TENANT_INFORMATION_KEY'];
const NURSING_HOMES_KEY: QueryKey = ['NURSING_HOMES_KEY'];
const NURSING_HOME_KEY = (nursingHomeId: string): QueryKey => [
  'NURSING_HOME_KEY',
  nursingHomeId,
];
const UPDATE_NURSING_HOME_KEY = (nursingHomeId: string): QueryKey => [
  'UPDATE_NURSING_HOME_KEY',
  nursingHomeId,
];
const MANGOPAY_SCA_ENROLLMENT_KEY: QueryKey = ['MANGOPAY_SCA_ENROLLMENT_KEY'];
const MANGOPAY_MANAGE_PROXY_CONSENT_KEY: QueryKey = [
  'MANGOPAY_MANAGE_PROXY_CONSENT_KEY',
];

export const useGetPaymentProcessor = (): 'stripe' | 'mangopay' => {
  const auth = useAuthentication(true);
  return auth.paymentProcessor ?? 'stripe';
};

export const useGetTenantInformation = (): UseQueryResult<
  {
    data: TenantInformation;
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    TENANT_INFORMATION_KEY,
    (api) => {
      return api.getTenantInformation();
    }
  );
};

export const useGetNursingHomes = (
  options?: Omit<
    UseQueryOptions<
      {
        data: NursingHome[];
      },
      unknown,
      {
        data: NursingHome[];
      }
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  {
    data: NursingHome[];
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    NURSING_HOMES_KEY,
    (api) => {
      return api.getNursingHomes();
    },
    options
  );
};

export const useGetNursingHomePhoenix = (
  nursingHomeId: string
): UseQueryResult<
  {
    data: NursingHome;
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    NURSING_HOME_KEY(nursingHomeId),
    (api) => {
      return api.getNursingHome({ params: { nursingHomeId } });
    }
  );
};

export const useUpdateNursingHomePhoenix = (
  nursingHomeId: string
): UseMutationResult<NursingHome, unknown, NursingHomeUpdate> => {
  const api = useNursingHomePhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<NursingHome, unknown, NursingHomeUpdate>({
    mutationKey: UPDATE_NURSING_HOME_KEY(nursingHomeId),
    mutationFn: (nursingHomeUpdate) =>
      api.updateNursingHome({
        params: { nursingHomeId },
        body: { data: nursingHomeUpdate },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: NURSING_HOME_KEY(nursingHomeId),
      });
    },
  });
  return result;
};

export const useGetLegalEntities = (
  options?: Omit<
    UseQueryOptions<
      {
        meta: {
          total_count: number;
        };
        data: LegalEntity[];
      },
      unknown,
      {
        meta: {
          total_count: number;
        };
        data: LegalEntity[];
      }
    >,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<
  {
    meta: {
      total_count: number;
    };
    data: LegalEntity[];
  },
  unknown
> => {
  return useApiQuery(
    useNursingHomePhoenixApi,
    LEGAL_ENTITIES_KEY,
    (api) => {
      return api.getLegalEntities();
    },
    {
      ...options,
    }
  );
};

export const useCreateLegalEntityPhoenix = (): UseMutationResult<
  {
    data: LegalEntity;
  },
  unknown,
  BaseData
> => {
  const api = useNursingHomePhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    {
      data: LegalEntity;
    },
    unknown,
    BaseData
  >({
    mutationKey: CREATE_LEGAL_ENTITY_KEY,
    mutationFn: (base_data) =>
      api.createLegalEntity({
        body: {
          data: {
            base_data,
          },
        },
      }),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY });
    },
  });
  return result;
};

export const useUpdateLegalEntityPhoenix = ({
  legalEntityId,
}: {
  legalEntityId: string;
}): UseMutationResult<
  {
    data: LegalEntity;
  },
  unknown,
  BaseData
> => {
  const api = useNursingHomePhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    {
      data: LegalEntity;
    },
    unknown,
    BaseData
  >({
    mutationKey: UPDATE_LEGAL_ENTITY_KEY,
    mutationFn: (base_data) =>
      api.updateLegalEntity({
        params: {
          legalEntityId,
        },
        body: {
          data: {
            id: legalEntityId,
            base_data,
          },
        },
      }),

    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY });
    },
  });
  return result;
};

const DELETE_LEGAL_ENTITY_CONFLICT_ERROR_MESSAGE =
  'Legal entity is still associated with nursing homes';

export const isDeleteLegalEntityConflictException = (
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  error: any
): boolean => {
  return (
    'errors' in error &&
    'detail' in error.errors &&
    error.errors.detail &&
    error.errors.detail === DELETE_LEGAL_ENTITY_CONFLICT_ERROR_MESSAGE
  );
};

export const useDeleteLegalEntityPhoenix = (): UseMutationResult<
  {
    success: boolean;
  },
  unknown,
  {
    legalEntityId: string;
  }
> => {
  const api = useNursingHomePhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    {
      success: boolean;
    },
    unknown,
    {
      legalEntityId: string;
    }
  >({
    mutationKey: ['UPDATE_LEGAL_ENTITY'],
    mutationFn: ({ legalEntityId }) =>
      api.deleteLegalEntity({
        params: {
          legalEntityId,
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: LEGAL_ENTITIES_KEY });
    },
  });
  return result;
};

export const useMangopayScaEnrollment = (): UseMutationResult<
  {
    redirect_url: string;
  },
  unknown,
  {
    legal_entity_id: string;
    return_url: string;
  }
> => {
  const api = useNursingHomePhoenixApi();
  const result = useMutation<
    {
      redirect_url: string;
    },
    unknown,
    {
      legal_entity_id: string;
      return_url: string;
    }
  >({
    mutationKey: MANGOPAY_SCA_ENROLLMENT_KEY,
    mutationFn: ({ legal_entity_id, return_url }) =>
      api.mangopayScaEnrollment({
        params: {
          legalEntityId: legal_entity_id,
        },
        body: {
          return_url,
        },
      }),
  });
  return result;
};

export const useMangopayManageProxyConsent = (): UseMutationResult<
  {
    redirect_url: string;
  },
  unknown,
  {
    legal_entity_id: string;
    return_url: string;
  }
> => {
  const api = useNursingHomePhoenixApi();
  const result = useMutation<
    {
      redirect_url: string;
    },
    unknown,
    {
      legal_entity_id: string;
      return_url: string;
    }
  >({
    mutationKey: MANGOPAY_MANAGE_PROXY_CONSENT_KEY,
    mutationFn: ({ legal_entity_id, return_url }) =>
      api.mangopayManageProxyConsent({
        params: {
          legalEntityId: legal_entity_id,
        },
        body: {
          return_url,
        },
      }),
  });
  return result;
};
